import JobCard from "./JobCard";
import JobCardSkeleton from "./JobCardSkeleton";
import AdvancedFilterPanel from "./AdvancedFilterPanel";
import ActiveFilterTags from "./ActiveFilterTags";
import SavedFilters from "./SavedFilters";
import { supabase } from "@/lib/supabase";
import { useState, useEffect, useMemo, useCallback } from "react";
import type { FilterState, SavedFilter } from "@/hooks/useFilterPersistence";

const quickFilters = [
  { value: "", label: "Tất cả" },
  { value: "Khu CN, Sản xuất, May mặc", label: "Sản xuất" },
  { value: "Việc làm phổ thông, Giúp việc", label: "Phổ thông" },
  { value: "Lái xe, Phụ xe, Giao hàng", label: "Lái xe" },
  { value: "Partime, Sinh viên", label: "Partime" },
  { value: "CNTT, Kỹ sư, Kỹ thuật", label: "CNTT" },
];

interface JobRow {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  location_key: string;
  is_hot: boolean;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  posted_at: string;
  expiry_date: string;
  approved_at: string | null;
  is_expired: boolean;
  posted_by: string;
  employer_email: string | null;
  employer_phone: string | null;
  khu_vuc: string | null;
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  category: string;
  locationKey: string;
  isHot: boolean;
  description: string;
  detail: string;
  requirements: string[];
  benefits: string[];
  postedAt: string;
  expiryDate: string;
  postedBy: string;
  employerEmail: string | null;
  employerPhone: string | null;
  khuVuc: string | null;
}

interface JobGridProps {
  searchQuery: string;
  activeCategory: string;
  activeLocation: string;
  activeLocationLabel: string;
  jobType: string;
  sortBy: string;
  onCategoryChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onJobTypeChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onClearAll: () => void;
  // Saved filters props
  currentFilters: FilterState;
  presets: SavedFilter[];
  matchingPreset: SavedFilter | null;
  isFilterEmpty: (filters: FilterState) => boolean;
  getFilterLabel: (filters: FilterState) => string;
  onSavePreset: (name: string, filters: FilterState) => SavedFilter;
  onDeletePreset: (id: string) => void;
  onApplyPreset: (filters: FilterState) => void;
}

const locations = [
  { value: "", label: "Tất cả địa điểm" },
  { value: "an giang", label: "An Giang" },
  { value: "bắc ninh", label: "Bắc Ninh" },
  { value: "cà mau", label: "Cà Mau" },
  { value: "cao bằng", label: "Cao Bằng" },
  { value: "đăk lawk", label: "Đắk Lắk" },
  { value: "điện biên", label: "Điện Biên" },
  { value: "đồng nai", label: "Đồng Nai" },
  { value: "đồng tháp", label: "Đồng Tháp" },
  { value: "gia lai", label: "Gia Lai" },
  { value: "hà tĩnh", label: "Hà Tĩnh" },
  { value: "hưng yên", label: "Hưng Yên" },
  { value: "khánh hòa", label: "Khánh Hòa" },
  { value: "lai châu", label: "Lai Châu" },
  { value: "lâm đồng", label: "Lâm Đồng" },
  { value: "lạng sơn", label: "Lạng Sơn" },
  { value: "lào cai", label: "Lào Cai" },
  { value: "nghệ an", label: "Nghệ An" },
  { value: "ninh bình", label: "Ninh Bình" },
  { value: "phú thọ", label: "Phú Thọ" },
  { value: "quảng ngãi", label: "Quảng Ngãi" },
  { value: "quang ninh", label: "Quảng Ninh" },
  { value: "quảng trị", label: "Quảng Trị" },
  { value: "sơn la", label: "Sơn La" },
  { value: "tây ninh", label: "Tây Ninh" },
  { value: "thái nguyên", label: "Thái Nguyên" },
  { value: "thanh hóa", label: "Thanh Hóa" },
  { value: "cần thơ", label: "Cần Thơ" },
  { value: "đà nẵng", label: "Đà Nẵng" },
  { value: "hà nội", label: "Hà Nội" },
  { value: "hải phòng", label: "Hải Phòng" },
  { value: "tp. hồ chí minh", label: "TP. Hồ Chí Minh" },
  { value: "huế", label: "Huế" },
  { value: "tuyên quang", label: "Tuyên Quang" },
  { value: "vĩnh long", label: "Vĩnh Long" },
];

function mapJob(row: JobRow): Job {
  return {
    id: row.id,
    title: row.title,
    company: row.company,
    location: row.location,
    salary: row.salary,
    type: row.type,
    category: row.category,
    locationKey: row.location_key,
    isHot: row.is_hot,
    description: row.description,
    detail: row.detail,
    requirements: row.requirements,
    benefits: row.benefits,
    postedAt: row.posted_at,
    expiryDate: row.expiry_date,
    postedBy: row.posted_by,
    employerEmail: row.employer_email,
    employerPhone: row.employer_phone,
    khuVuc: row.khu_vuc,
  };
}

// Extract numeric salary value for sorting (returns estimated monthly VND in millions)
function extractSalaryValue(salary: string): number {
  if (!salary) return 0;
  const normalized = salary.toLowerCase().trim();

  // "Thỏa thuận" / "negotiable" → treat as 0 (sort last when high→low)
  if (normalized.includes("thỏa thuận") || normalized.includes("thương lượng") || normalized.includes("negotiable")) {
    return 0;
  }

  if (normalized.includes("triệu")) {
    const match = normalized.match(/(\d+(?:[.,]\d+)?)/);
    if (match) return parseFloat(match[1].replace(",", "."));
  }

  if (normalized.includes("usd") || normalized.includes("$")) {
    const match = normalized.match(/(\d+(?:[.,]\d+)?)/);
    if (match) return parseFloat(match[1].replace(",", ".")) * 25;
  }

  if (normalized.includes("vnđ") || normalized.includes("vnd")) {
    const match = normalized.match(/(\d+(?:[.,]\d+)?)/);
    if (match) return parseFloat(match[1].replace(",", ".")) / 1000000;
  }

  // Fallback: try to extract any number
  const match = normalized.match(/(\d+(?:[.,]\d+)?)/);
  if (match) return parseFloat(match[1].replace(",", "."));

  return 0;
}

export default function JobGrid({
  searchQuery,
  activeCategory,
  activeLocation,
  activeLocationLabel,
  jobType,
  sortBy,
  onCategoryChange,
  onSearchChange,
  onLocationChange,
  onJobTypeChange,
  onSortChange,
  onClearAll,
  currentFilters,
  presets,
  matchingPreset,
  isFilterEmpty,
  getFilterLabel,
  onSavePreset,
  onDeletePreset,
  onApplyPreset,
}: JobGridProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [prevFilters, setPrevFilters] = useState({ category: activeCategory, location: activeLocation, jobType, sortBy, searchQuery });

  const fetchJobs = useCallback(async () => {
    try {
      setError(null);
      const fortyDaysAgo = new Date();
      fortyDaysAgo.setDate(fortyDaysAgo.getDate() - 40);
      const fortyDaysAgoStr = fortyDaysAgo.toISOString().split("T")[0];

      const thirtyFiveDaysAgo = new Date();
      thirtyFiveDaysAgo.setDate(thirtyFiveDaysAgo.getDate() - 35);

      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_jobs")
        .select("*")
        .eq("status", "approved")
        .gte("posted_at", fortyDaysAgoStr)
        .or("is_expired.is.null,is_expired.eq.false")
        .order("posted_at", { ascending: false });

      if (supabaseError) {
        setError("Không thể tải dữ liệu việc làm. Vui lòng thử lại.");
        return;
      }

      if (data) {
        const filtered = (data as JobRow[]).filter((job) => {
          if (job.is_expired) return false;
          if (job.approved_at) {
            const approvedDate = new Date(job.approved_at);
            return approvedDate > thirtyFiveDaysAgo;
          }
          return true;
        }).map(mapJob);
        setJobs(filtered);
      }
    } catch {
      setError("Không thể tải dữ liệu việc làm. Vui lòng thử lại.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Show brief loading when filters change
  useEffect(() => {
    const currentFilters = { category: activeCategory, location: activeLocation, jobType, sortBy, searchQuery };
    if (JSON.stringify(currentFilters) !== JSON.stringify(prevFilters)) {
      setFilterLoading(true);
      setPrevFilters(currentFilters);
      const timer = setTimeout(() => setFilterLoading(false), 300);
      return () => clearTimeout(timer);
    }
  }, [activeCategory, activeLocation, jobType, sortBy, searchQuery, prevFilters]);

  const filteredJobs = useMemo(() => {
    let result = jobs.filter((job) => {
      // Category filter
      const matchesCategory =
        activeCategory === "" || job.category.includes(activeCategory);

      // Location filter
      const matchesLocation =
        activeLocation === "" || job.locationKey === activeLocation;

      // Job type filter
      const matchesType =
        jobType === "" || job.type === jobType;

      // Search query (case-insensitive, search across multiple fields)
      let matchesSearch = true;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        matchesSearch =
          job.title.toLowerCase().includes(q) ||
          job.company.toLowerCase().includes(q) ||
          job.description.toLowerCase().includes(q) ||
          job.detail.toLowerCase().includes(q) ||
          job.location.toLowerCase().includes(q) ||
          (job.khuVuc && job.khuVuc.toLowerCase().includes(q));
      }

      return matchesCategory && matchesLocation && matchesType && matchesSearch;
    });

    // Sort
    if (sortBy === "oldest") {
      result = [...result].sort((a, b) => a.postedAt.localeCompare(b.postedAt));
    } else if (sortBy === "salary-high") {
      result = [...result].sort((a, b) => extractSalaryValue(b.salary) - extractSalaryValue(a.salary));
    } else if (sortBy === "salary-low") {
      result = [...result].sort((a, b) => extractSalaryValue(a.salary) - extractSalaryValue(b.salary));
    }
    // "newest" is default — data already comes sorted by posted_at desc from DB

    return result;
  }, [jobs, activeCategory, activeLocation, jobType, sortBy, searchQuery]);

  const handleClearAllFilters = () => {
    onClearAll();
  };

  if (error) {
    return (
      <section id="jobs" className="py-16 md:py-20 px-4 md:px-10">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-2xl text-red-500"></i>
          </div>
          <p className="text-foreground-700 font-medium mb-4">{error}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              fetchJobs();
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-base"></i>
            <span>Thử lại</span>
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="jobs" className="py-16 md:py-20 px-4 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-12">
          <span className="inline-block px-3 py-1 bg-accent-100/80 text-accent-700 text-xs font-semibold rounded-full mb-4">
            CƠ HỘI VIỆC LÀM
          </span>
          <h2 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-3">
            Việc làm mới nhất hôm nay
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Hàng trăm việc làm được cập nhật mỗi ngày từ các công ty uy tín trên toàn quốc
          </p>
        </div>

        {/* Quick filter chips */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
          {quickFilters.map((f) => (
            <button
              key={f.value}
              onClick={() => onCategoryChange(f.value)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === f.value
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800 border border-background-200/70"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Advanced filter panel */}
        <AdvancedFilterPanel
          jobType={jobType}
          sortBy={sortBy}
          onJobTypeChange={onJobTypeChange}
          onSortChange={onSortChange}
        />

        {/* Saved filter presets & save button */}
        <SavedFilters
          currentFilters={currentFilters}
          presets={presets}
          matchingPreset={matchingPreset}
          isFilterEmpty={isFilterEmpty}
          getFilterLabel={getFilterLabel}
          onSavePreset={onSavePreset}
          onDeletePreset={onDeletePreset}
          onApplyPreset={onApplyPreset}
        />

        {/* Active filter tags */}
        <ActiveFilterTags
          searchQuery={searchQuery}
          category={activeCategory}
          location={activeLocation}
          locationLabel={activeLocationLabel}
          jobType={jobType}
          sortBy={sortBy}
          onClearSearch={() => onSearchChange("")}
          onClearCategory={() => onCategoryChange("")}
          onClearLocation={() => onLocationChange("")}
          onClearJobType={() => onJobTypeChange("")}
          onClearSort={() => onSortChange("newest")}
          onClearAll={handleClearAllFilters}
        />

        {/* Results count */}
        {!isLoading && !filterLoading && (
          <div className="mb-6">
            <p className="text-sm text-foreground-500">
              <span className="font-semibold text-foreground-800">{filteredJobs.length}</span> việc làm được tìm thấy
              {searchQuery && (
                <span> cho <span className="font-medium text-foreground-700">"{searchQuery}"</span></span>
              )}
            </p>
          </div>
        )}

        {isLoading || filterLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {Array.from({ length: 9 }).map((_, i) => (
              <JobCardSkeleton key={i} />
            ))}
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200/70 flex items-center justify-center">
              <i className="ri-file-search-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 text-sm mb-6">
              Không tìm thấy việc làm phù hợp. Hãy thử bộ lọc khác nhé!
            </p>
            <button
              onClick={handleClearAllFilters}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-base"></i>
              Xóa tất cả bộ lọc
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredJobs.map((job, index) => (
              <div
                key={job.id}
                className="opacity-0 animate-card-enter"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <JobCard job={job} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}